// The stored analytics consent (flag REACT_APP_CLARITY_CONSENT_ENABLED).
// Storing the choice itself needs no consent (strictly necessary, no
// tracking). The bootstrap script in public/index.html reads the SAME key,
// JSON shape and max age before React exists - the two readers are pinned
// against each other by a source-text test. The key must never end in
// _products or _activeOrder (RouteErrorView's cart reset deletes those).
export type ClarityConsentState = "granted" | "denied" | "unset";
export type ClarityConsentChoice = Exclude<ClarityConsentState, "unset">;

export const CONSENT_STORAGE_KEY = "kitmaker.consent.v1";
// "1" while the last signed-in user of this browser was internal (admin,
// act-as, internal e-mail) with REACT_APP_CLARITY_RECORD_INTERNAL off. The
// bootstrap in public/index.html reads it and does NOT load the tags on a
// stored "granted" - otherwise the recorder uploads the first seconds
// (URL, screen) before the profile arrives and the facade can stop it. The
// facade clears it as soon as a non-internal identity is known and loads
// the tags then. Nothing is stored about who the user is - one bit.
export const CONSENT_INTERNAL_STORAGE_KEY = "kitmaker.consent.internal";
// window CustomEvent dispatched by writeConsent so a mounted banner drops
// out when the choice is made elsewhere (the Cookie settings dialog).
export const CONSENT_CHANGED_EVENT = "kitmaker:consent";
// Bump when the policy changes (new purposes): everyone sees the banner again.
export const CONSENT_VERSION = 1;
// Clarity treats consent as valid for at most 9 months; Danish practice
// asks for a renewal at least every 12. Same number in index.html.
export const CONSENT_MAX_AGE_DAYS = 270;
const DAY_MS = 864e5;

export interface StoredConsent {
  v: typeof CONSENT_VERSION;
  analytics: ClarityConsentChoice;
  at: string; // ISO timestamp of the choice
}

function isChoice(value: unknown): value is ClarityConsentChoice {
  return value === "granted" || value === "denied";
}

// "unset" for anything but a well-formed, current-version, unexpired entry.
export function readConsent(now: number = Date.now()): ClarityConsentState {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return "unset";
    const parsed = JSON.parse(raw) as Partial<StoredConsent> | null;
    if (!parsed || parsed.v !== CONSENT_VERSION) return "unset";
    if (!isChoice(parsed.analytics)) return "unset";
    if (typeof parsed.at !== "string") return "unset";
    const at = Date.parse(parsed.at);
    if (Number.isNaN(at)) return "unset";
    if (now - at >= CONSENT_MAX_AGE_DAYS * DAY_MS) return "unset";
    return parsed.analytics;
  } catch {
    return "unset";
  }
}

export function writeConsent(state: ClarityConsentChoice): void {
  try {
    const stored: StoredConsent = {
      v: CONSENT_VERSION,
      analytics: state,
      at: new Date().toISOString(),
    };
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode, quota): the choice lives for this
    // page only; the banner asks again next time.
  }
  try {
    window.dispatchEvent(
      new CustomEvent<ClarityConsentChoice>(CONSENT_CHANGED_EVENT, {
        detail: state,
      }),
    );
  } catch {
    // No listeners / no CustomEvent: nothing to notify.
  }
}

export function readInternalMarker(): boolean {
  try {
    return window.localStorage.getItem(CONSENT_INTERNAL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeInternalMarker(internal: boolean): void {
  try {
    if (internal) {
      window.localStorage.setItem(CONSENT_INTERNAL_STORAGE_KEY, "1");
    } else {
      window.localStorage.removeItem(CONSENT_INTERNAL_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: the bootstrap then behaves as for a new browser.
  }
}

export function clearConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
